import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import {
  useBuyPhoneNumberMutation,
  useConnectPhoneNumberMutation,
  useCreateFaqMutation,
  useCreateLocationMutation,
  useCreateRestaurantMutation,
  useGetFaqsQuery,
  useGetLocationQuery,
  useGetOnboardingQuery,
  useGetPromptsQuery,
  useGetRestaurantsQuery,
  useGoLiveMutation,
  useLazyLookupPhoneNumberQuery,
  useLazySearchPhoneNumbersQuery,
  useSaveOnboardingStepMutation,
  useSavePromptMutation,
  useSetBusinessHoursMutation,
  useUpdateFaqMutation,
  useUpdateLocationMutation,
  useUpdateRestaurantMutation
} from '@/store/configApi';
import { Button, Field, Panel } from '@/components/ui';
import { defaultWeek, HoursEditor, type DayHours } from '@/components/forms/HoursEditor';
import { formatMoney, formatPhone, toE164 } from '@/utils/format';

/**
 * Setup runs as a wizard because the order matters: the phone line cannot go
 * live until it knows the hours, has somewhere to transfer to, and owns a
 * number. Each step saves as it completes, so an interrupted setup resumes.
 */
const STEPS = [
  { id: 1, label: 'Restaurant' },
  { id: 2, label: 'Location' },
  { id: 3, label: 'Opening hours' },
  { id: 4, label: 'Service details' },
  { id: 5, label: 'Common questions' },
  { id: 6, label: 'Greeting' },
  { id: 7, label: 'Phone number' },
  { id: 8, label: 'Go live' }
] as const;

const STARTER_FAQS = [
  {
    canonicalQuestion: 'Do you offer catering?',
    acceptedPhrases: ['do you cater', 'catering options'],
    answer: ''
  },
  {
    canonicalQuestion: 'Do you accept reservations?',
    acceptedPhrases: ['can i book a table', 'make a reservation'],
    answer: ''
  },
  {
    canonicalQuestion: 'Is there parking?',
    acceptedPhrases: ['where can i park', 'do you have parking'],
    answer: ''
  }
];

export function OnboardingWizard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const progress = useGetOnboardingQuery();
  const [saveStep] = useSaveOnboardingStepMutation();

  const [step, setStep] = useState(1);
  const [restaurantId, setRestaurantId] = useState('');
  const [locationId, setLocationId] = useState('');

  const savedStep = progress.data?.step;
  useEffect(() => {
    if (savedStep) setStep((current) => Math.max(current, savedStep));
  }, [savedStep]);

  // A reload drops the ids the earlier steps handed us, but the saved step
  // sends the user straight back to step 7. Recover them from the server so
  // those steps do not post an empty locationId.
  const savedRestaurantId = progress.data?.restaurantId;
  const savedLocationId = progress.data?.locationId;
  useEffect(() => {
    if (savedRestaurantId) setRestaurantId((current) => current || savedRestaurantId);
    if (savedLocationId) setLocationId((current) => current || savedLocationId);
  }, [savedRestaurantId, savedLocationId]);

  const advance = async (next: number) => {
    setStep(next);
    // Re-saving an earlier step would rewind where a reload drops the user, so
    // the furthest step reached is what gets stored.
    const furthest = Math.max(next, savedStep ?? 0);
    if (furthest !== savedStep) {
      await saveStep({ step: furthest }).unwrap().catch(() => undefined);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-8">
        <h1 className="text-display">Set up your phone line</h1>
        <p className="mt-1 max-w-xl text-small text-slate">
          Eight short steps. Everything you enter here is what the line is allowed to say —
          it will never make up an answer you have not given it.
        </p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[200px_1fr]">
        <nav aria-label="Setup steps">
          <ol className="space-y-1">
            {STEPS.map((entry) => {
              const state = entry.id === step ? 'current' : entry.id < step ? 'done' : 'todo';
              return (
                <li key={entry.id}>
                  <button
                    onClick={() => entry.id < step && setStep(entry.id)}
                    disabled={entry.id > step}
                    className={`flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left text-small transition-colors ${
                      state === 'current'
                        ? 'bg-ink text-white'
                        : state === 'done'
                          ? 'text-ink hover:bg-white'
                          : 'text-slate-light'
                    }`}
                  >
                    <span
                      className={`tabular flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-micro ${
                        state === 'done'
                          ? 'bg-jade text-white'
                          : state === 'current'
                            ? 'bg-white text-ink'
                            : 'bg-rule text-slate'
                      }`}
                    >
                      {state === 'done' ? '✓' : entry.id}
                    </span>
                    {entry.label}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div>
          {step === 1 ? (
            <RestaurantStep
              restaurantId={restaurantId}
              onDone={(id) => {
                setRestaurantId(id);
                void advance(2);
              }}
            />
          ) : null}

          {step === 2 ? (
            <LocationStep
              restaurantId={restaurantId}
              locationId={locationId}
              onDone={(id) => {
                setLocationId(id);
                void advance(3);
              }}
            />
          ) : null}

          {step === 3 ? (
            <HoursStep locationId={locationId} onDone={() => void advance(4)} />
          ) : null}

          {step === 4 ? (
            <DetailsStep locationId={locationId} onDone={() => void advance(5)} />
          ) : null}

          {step === 5 ? (
            <FaqStep locationId={locationId} onDone={() => void advance(6)} />
          ) : null}

          {step === 6 ? (
            <GreetingStep locationId={locationId} onDone={() => void advance(7)} />
          ) : null}

          {step === 7 ? (
            <PhoneStep locationId={locationId} onDone={() => void advance(8)} />
          ) : null}

          {step === 8 ? (
            <GoLiveStep
              locationId={locationId}
              onDone={() => {
                dispatch(pushToast('Your phone line is live.', 'success'));
                navigate('/');
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ step 1 */

function RestaurantStep({
  restaurantId,
  onDone
}: {
  restaurantId: string;
  onDone: (id: string) => void;
}) {
  const [create, createState] = useCreateRestaurantMutation();
  const [update, updateState] = useUpdateRestaurantMutation();
  const restaurants = useGetRestaurantsQuery();
  const [form, setForm] = useState({ name: '', cuisine: '', mainPhone: '' });
  const [error, setError] = useState<string | null>(null);

  // Coming back to this step must show what was saved, not an empty form.
  const saved =
    restaurants.data?.data.find((entry) => entry._id === restaurantId) ??
    restaurants.data?.data[0];
  const savedId = saved?._id;
  useEffect(() => {
    if (!saved) return;
    setForm({
      name: saved.name,
      cuisine: saved.cuisine ?? '',
      mainPhone: saved.mainPhone ?? ''
    });
  }, [savedId]);

  const submit = async () => {
    if (!form.name.trim()) return setError('Enter the name customers know you by.');
    const body = {
      name: form.name,
      cuisine: form.cuisine || undefined,
      mainPhone: form.mainPhone || undefined
    };

    // Editing an existing restaurant, never a second copy of it.
    const result = savedId ? await update({ id: savedId, ...body }) : await create(body);
    if ('error' in result) return setError('Could not save. Check the fields and try again.');
    onDone(savedId ?? result.data._id);
  };

  const isLoading = createState.isLoading || updateState.isLoading;

  return (
    <Panel title="About your restaurant">
      <div className="space-y-4 px-4 py-4">
        <Field
          label="Restaurant name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          placeholder="Spice Route"
        />
        <Field
          label="Type of food"
          value={form.cuisine}
          onChange={(event) => setForm({ ...form, cuisine: event.target.value })}
          placeholder="South Indian"
        />
        <Field
          label="Your current phone number"
          value={form.mainPhone}
          onChange={(event) => setForm({ ...form, mainPhone: event.target.value })}
          placeholder="416 555 0100"
        />
        <p className="text-small text-slate">
          This is the number on your signage. You will keep it and forward it to the line
          we set up in a later step.
        </p>

        {error ? <p className="text-small text-rust">{error}</p> : null}

        <Button loading={isLoading} onClick={submit}>
          Continue
        </Button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 2 */

function LocationStep({
  restaurantId,
  locationId,
  onDone
}: {
  restaurantId: string;
  locationId: string;
  onDone: (id: string) => void;
}) {
  const [create, createState] = useCreateLocationMutation();
  const [update, updateState] = useUpdateLocationMutation();
  const saved = useGetLocationQuery(locationId, { skip: !locationId });
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    addressLine1: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'CA',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto',
    transferNumber: ''
  });

  const savedLocation = saved.data;
  useEffect(() => {
    if (!savedLocation) return;
    setForm((current) => ({
      ...current,
      name: savedLocation.name,
      addressLine1: savedLocation.addressLine1,
      city: savedLocation.city,
      region: savedLocation.region,
      postalCode: savedLocation.postalCode,
      country: savedLocation.country,
      timezone: savedLocation.timezone,
      transferNumber: savedLocation.transferNumber ?? ''
    }));
  }, [savedLocation?._id]);

  const submit = async () => {
    if (!restaurantId && !locationId) {
      return setError('Go back a step and save your restaurant first.');
    }
    const missing = ['name', 'addressLine1', 'city', 'region', 'postalCode'].filter(
      (field) => !form[field as keyof typeof form]
    );
    if (missing.length > 0) return setError('Fill in the address fields before continuing.');

    // Second pass over this step edits the location instead of adding another.
    const result = locationId
      ? await update({ id: locationId, ...form })
      : await create({ restaurantId, ...form });
    if ('error' in result) return setError('Could not save this location.');
    onDone(locationId || result.data._id);
  };

  const isLoading = createState.isLoading || updateState.isLoading;

  return (
    <Panel title="Where is this location?">
      <div className="space-y-4 px-4 py-4">
        <Field
          label="Location name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          placeholder="King Street West"
        />
        <Field
          label="Street address"
          value={form.addressLine1}
          onChange={(event) => setForm({ ...form, addressLine1: event.target.value })}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="City"
            value={form.city}
            onChange={(event) => setForm({ ...form, city: event.target.value })}
          />
          <Field
            label="Province or state"
            value={form.region}
            onChange={(event) => setForm({ ...form, region: event.target.value })}
          />
          <Field
            label="Postal code"
            value={form.postalCode}
            onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
          />
        </div>

        <Field
          label="Transfer calls to"
          value={form.transferNumber}
          onChange={(event) => setForm({ ...form, transferNumber: event.target.value })}
          placeholder="416 555 0100"
        />
        <p className="text-small text-slate">
          When the line hears something it was not set up for, it sends the caller to this
          number instead of guessing. This is required before you can go live.
        </p>

        {error ? <p className="text-small text-rust">{error}</p> : null}

        <Button loading={isLoading} onClick={submit}>
          Continue
        </Button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 3 */

function HoursStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const [days, setDays] = useState<DayHours[]>(defaultWeek());
  const [save, { isLoading }] = useSetBusinessHoursMutation();
  const saved = useGetLocationQuery(locationId, { skip: !locationId });
  const dispatch = useAppDispatch();

  // Hours already on file win over the 11-to-10 default week.
  const savedHours = saved.data?.hours;
  useEffect(() => {
    if (!savedHours?.length) return;
    setDays(
      defaultWeek().map((day) => {
        const match = savedHours.find((entry) => entry.weekday === day.weekday);
        return match ? { weekday: day.weekday, closed: match.closed, windows: match.windows } : day;
      })
    );
  }, [savedHours]);

  const submit = async () => {
    const result = await save({ id: locationId, days });
    if ('error' in result) {
      dispatch(pushToast('Could not save your hours.', 'error'));
      return;
    }
    onDone();
  };

  return (
    <Panel title="When are you open?">
      <div className="space-y-4 px-4 py-4">
        <p className="text-small text-slate">
          Callers who ring outside these hours hear that you are closed, along with the
          times below. If your kitchen runs past midnight, set the closing time after
          midnight and we will handle it.
        </p>
        <HoursEditor value={days} onChange={setDays} />
        <Button loading={isLoading} onClick={submit}>
          Save and continue
        </Button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 4 */

const PAYMENT_METHODS = ['Visa', 'Mastercard', 'American Express', 'debit', 'cash'];
const SERVICE_TYPES = [
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'DINE_IN', label: 'Dine in' }
];

function DetailsStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const [update, { isLoading }] = useUpdateLocationMutation();
  const saved = useGetLocationQuery(locationId, { skip: !locationId });
  const dispatch = useAppDispatch();
  const [form, setForm] = useState({
    fulfillment: ['PICKUP'] as string[],
    acceptedPaymentMethods: ['Visa', 'Mastercard', 'debit'] as string[],
    averagePrepTimeMinutes: 20,
    deliveryInfo: '',
    parkingInfo: '',
    orderingEnabled: false
  });

  const savedLocation = saved.data;
  useEffect(() => {
    if (!savedLocation) return;
    setForm({
      fulfillment: savedLocation.fulfillment?.length ? savedLocation.fulfillment : ['PICKUP'],
      acceptedPaymentMethods: savedLocation.acceptedPaymentMethods ?? [],
      averagePrepTimeMinutes: savedLocation.averagePrepTimeMinutes ?? 20,
      deliveryInfo: savedLocation.deliveryInfo ?? '',
      parkingInfo: savedLocation.parkingInfo ?? '',
      orderingEnabled: savedLocation.orderingEnabled ?? false
    });
  }, [savedLocation?._id]);

  const toggle = (field: 'fulfillment' | 'acceptedPaymentMethods', value: string) => {
    const current = form[field];
    setForm({
      ...form,
      [field]: current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
    });
  };

  const submit = async () => {
    const result = await update({ id: locationId, ...form });
    if ('error' in result) {
      dispatch(pushToast('Could not save these details.', 'error'));
      return;
    }
    onDone();
  };

  return (
    <Panel title="How you serve customers">
      <div className="space-y-5 px-4 py-4">
        <fieldset>
          <legend className="text-small font-medium text-ink">What you offer</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {SERVICE_TYPES.map((type) => (
              <label key={type.value} className="flex items-center gap-2 text-small text-slate">
                <input
                  type="checkbox"
                  checked={form.fulfillment.includes(type.value)}
                  onChange={() => toggle('fulfillment', type.value)}
                  className="h-4 w-4 accent-[#0B7A5A]"
                />
                {type.label}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-small font-medium text-ink">Payment you accept</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {PAYMENT_METHODS.map((method) => (
              <label key={method} className="flex items-center gap-2 text-small text-slate">
                <input
                  type="checkbox"
                  checked={form.acceptedPaymentMethods.includes(method)}
                  onChange={() => toggle('acceptedPaymentMethods', method)}
                  className="h-4 w-4 accent-[#0B7A5A]"
                />
                {method}
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          label="Typical time to prepare an order (minutes)"
          type="number"
          value={form.averagePrepTimeMinutes}
          onChange={(event) =>
            setForm({ ...form, averagePrepTimeMinutes: Number(event.target.value) })
          }
        />

        <div className="space-y-1.5">
          <label htmlFor="deliveryInfo" className="block text-small font-medium text-ink">
            What to tell callers who ask about delivery
          </label>
          <textarea
            id="deliveryInfo"
            rows={2}
            value={form.deliveryInfo}
            onChange={(event) => setForm({ ...form, deliveryInfo: event.target.value })}
            placeholder="We deliver within five kilometres, usually in forty five minutes."
            className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
          />
          <p className="text-micro text-slate">
            Left blank, the line answers only yes or no based on what you ticked above.
          </p>
        </div>

        <label className="flex items-start gap-2 rounded bg-paper px-3 py-2.5 text-small text-slate">
          <input
            type="checkbox"
            checked={form.orderingEnabled}
            onChange={(event) => setForm({ ...form, orderingEnabled: event.target.checked })}
            className="mt-0.5 h-4 w-4 accent-[#0B7A5A]"
          />
          <span>
            <span className="font-medium text-ink">Take orders over the phone</span>
            <br />
            Turn this on once your menu is loaded. Until then the line answers questions and
            passes ordering calls to your staff.
          </span>
        </label>

        <Button loading={isLoading} onClick={submit}>
          Save and continue
        </Button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 5 */

function FaqStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const [create, createState] = useCreateFaqMutation();
  const [update, updateState] = useUpdateFaqMutation();
  const saved = useGetFaqsQuery(locationId, { skip: !locationId });
  const [answers, setAnswers] = useState(STARTER_FAQS);
  const dispatch = useAppDispatch();

  const savedFaqs = saved.data?.data;
  useEffect(() => {
    if (!savedFaqs?.length) return;
    setAnswers(
      STARTER_FAQS.map((faq) => {
        const match = savedFaqs.find(
          (entry) => entry.canonicalQuestion === faq.canonicalQuestion
        );
        return match ? { ...faq, answer: match.answer } : faq;
      })
    );
  }, [savedFaqs]);

  const submit = async () => {
    const filled = answers.filter((faq) => faq.answer.trim().length > 0);

    for (const faq of filled) {
      // Editing an answer written earlier must not add a second copy of it.
      const existing = savedFaqs?.find(
        (entry) => entry.canonicalQuestion === faq.canonicalQuestion
      );
      const result = existing
        ? await update({ id: existing._id, answer: faq.answer.trim(), enabled: true })
        : await create({
            locationId,
            canonicalQuestion: faq.canonicalQuestion,
            acceptedPhrases: faq.acceptedPhrases,
            answer: faq.answer.trim(),
            enabled: true,
            priority: 5
          });
      if ('error' in result) {
        dispatch(pushToast('Could not save one of your answers.', 'error'));
        return;
      }
    }

    onDone();
  };

  const isLoading = createState.isLoading || updateState.isLoading;

  return (
    <Panel title="Questions callers ask">
      <div className="space-y-5 px-4 py-4">
        <p className="text-small text-slate">
          Write the answer in your own words. The line reads it back exactly as you type it.
          Anything you leave blank is a question it will pass to your staff instead.
        </p>

        {answers.map((faq, index) => (
          <div key={faq.canonicalQuestion} className="space-y-1.5">
            <label htmlFor={`faq-${index}`} className="block text-small font-medium text-ink">
              {faq.canonicalQuestion}
            </label>
            <textarea
              id={`faq-${index}`}
              rows={2}
              value={faq.answer}
              onChange={(event) =>
                setAnswers(
                  answers.map((entry, entryIndex) =>
                    entryIndex === index ? { ...entry, answer: event.target.value } : entry
                  )
                )
              }
              className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            />
          </div>
        ))}

        <div className="flex items-center gap-3">
          <Button loading={isLoading} onClick={submit}>
            Save and continue
          </Button>
          <button onClick={onDone} className="text-small text-slate hover:text-ink">
            Add these later
          </button>
        </div>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 6 */

function GreetingStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const [save, { isLoading }] = useSavePromptMutation();
  const saved = useGetPromptsQuery(locationId, { skip: !locationId });
  const dispatch = useAppDispatch();
  const [text, setText] = useState('Thanks for calling. How can I help?');

  const savedGreeting = saved.data?.data.find((prompt) => prompt.key === 'GREETING');
  useEffect(() => {
    if (savedGreeting?.customised && savedGreeting.text) setText(savedGreeting.text);
  }, [savedGreeting?.text, savedGreeting?.customised]);

  const submit = async () => {
    const result = await save({ locationId, key: 'GREETING', text });
    if ('error' in result) {
      dispatch(pushToast('Could not save your greeting.', 'error'));
      return;
    }
    onDone();
  };

  return (
    <Panel title="What callers hear first">
      <div className="space-y-4 px-4 py-4">
        <textarea
          rows={3}
          value={text}
          onChange={(event) => setText(event.target.value)}
          className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
          aria-label="Greeting"
        />
        <p className="text-small text-slate">
          Keep it short. Callers interrupt long greetings, and the line listens as soon as it
          finishes speaking. You can change every other phrase it uses under Voice.
        </p>

        <Button loading={isLoading} onClick={submit}>
          Save and continue
        </Button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 7 */

function PhoneStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [areaCode, setAreaCode] = useState('');
  const [search, searchState] = useLazySearchPhoneNumbersQuery();
  const [buy, buyState] = useBuyPhoneNumberMutation();
  const [connect, connectState] = useConnectPhoneNumberMutation();
  const [lookup, lookupState] = useLazyLookupPhoneNumberQuery();
  const [existing, setExisting] = useState({ phoneNumber: '', telnyxNumberId: '' });
  const dispatch = useAppDispatch();

  const claim = async (phoneNumber: string) => {
    const result = await buy({ phoneNumber, locationId });
    if ('error' in result) {
      dispatch(pushToast('Could not set up that number.', 'error'));
      return;
    }
    dispatch(pushToast(`${formatPhone(phoneNumber)} is yours.`, 'success'));
    onDone();
  };

  // The id lives in the Telnyx portal; fetch it from the number so nobody has
  // to go and copy a UUID by hand.
  const fetchId = async () => {
    const phoneNumber = toE164(existing.phoneNumber);
    if (!phoneNumber) {
      dispatch(pushToast('Enter the phone number first.', 'error'));
      return;
    }

    const result = await lookup({ phoneNumber });
    if (result.isError || !result.data) {
      dispatch(pushToast('That number is not in your Telnyx account.', 'error'));
      return;
    }

    setExisting({ phoneNumber, telnyxNumberId: result.data.telnyxNumberId });
    dispatch(pushToast('Found it — the ID is filled in.', 'success'));
  };

  const attach = async () => {
    const phoneNumber = toE164(existing.phoneNumber);
    if (!phoneNumber) {
      dispatch(pushToast('Enter the phone number first.', 'error'));
      return;
    }

    // A blank id is fine: the server resolves it from the number.
    const result = await connect({
      phoneNumber,
      telnyxNumberId: existing.telnyxNumberId || undefined,
      locationId
    });
    if ('error' in result) {
      dispatch(pushToast('Could not connect that number.', 'error'));
      return;
    }
    onDone();
  };

  return (
    <Panel title="The number your line answers on">
      <div className="space-y-4 px-4 py-4">
        <div className="flex gap-2">
          {(
            [
              { value: 'new', label: 'Get a new number' },
              { value: 'existing', label: 'Use a Telnyx number I own' }
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              onClick={() => setMode(option.value)}
              className={`rounded-sm border px-2.5 py-1 text-small ${
                mode === option.value
                  ? 'border-ink bg-ink text-white'
                  : 'border-rule bg-white text-slate hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {mode === 'new' ? (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-40">
                <Field
                  label="Area code"
                  value={areaCode}
                  onChange={(event) => setAreaCode(event.target.value)}
                  placeholder="416"
                />
              </div>
              <Button
                variant="secondary"
                loading={searchState.isFetching}
                onClick={() => search({ areaCode: areaCode || undefined, limit: 10 })}
              >
                Find numbers
              </Button>
            </div>

            {searchState.isError ? (
              <p className="rounded bg-rust-soft px-3 py-2 text-small text-rust">
                Number search is unavailable. Check that your Telnyx key is set on the server.
              </p>
            ) : null}

            {searchState.data ? (
              searchState.data.data.length === 0 ? (
                <p className="text-small text-slate">
                  No numbers free in that area code. Try a nearby one.
                </p>
              ) : (
                <ul className="divide-y divide-rule border-y border-rule">
                  {searchState.data.data.map((number) => (
                    <li key={number.phoneNumber} className="flex items-center gap-3 py-2.5">
                      <span className="tabular text-small text-ink">
                        {formatPhone(number.phoneNumber)}
                      </span>
                      <span className="text-small text-slate">{number.locality ?? ''}</span>
                      <span className="tabular ml-auto text-small text-slate">
                        {formatMoney(number.monthlyCostCents)}/month
                      </span>
                      <Button loading={buyState.isLoading} onClick={() => claim(number.phoneNumber)}>
                        Use this
                      </Button>
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </>
        ) : (
          <div className="space-y-4">
            <Field
              label="Phone number"
              value={existing.phoneNumber}
              onChange={(event) => setExisting({ ...existing, phoneNumber: event.target.value })}
              onBlur={(event) =>
                setExisting((current) => ({
                  ...current,
                  phoneNumber: toE164(event.target.value)
                }))
              }
              placeholder="+14165550123"
            />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field
                  label="Telnyx number ID"
                  value={existing.telnyxNumberId}
                  onChange={(event) =>
                    setExisting({ ...existing, telnyxNumberId: event.target.value })
                  }
                  placeholder="Filled in for you"
                />
              </div>
              <Button variant="secondary" loading={lookupState.isFetching} onClick={fetchId}>
                Get ID
              </Button>
            </div>
            <p className="text-small text-slate">
              Leave the ID blank and we will find it from your Telnyx account, or press Get ID
              to see it first. We will point the number at your line.
            </p>
            <Button loading={connectState.isLoading} onClick={attach}>
              Connect this number
            </Button>
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ step 8 */

const CHECK_LABELS: Record<string, string> = {
  restaurant: 'Restaurant details saved',
  location: 'Location and address saved',
  hours: 'At least one day of opening hours',
  faqs: 'At least one answer written',
  phoneNumber: 'A phone number assigned',
  transferNumber: 'A number to transfer callers to'
};

function GoLiveStep({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const progress = useGetOnboardingQuery();
  const [goLive, { isLoading }] = useGoLiveMutation();
  const dispatch = useAppDispatch();

  const checks = progress.data?.checks;
  const blocking = checks
    ? Object.entries(checks).filter(([key, passed]) => !passed && key !== 'faqs')
    : [];

  const submit = async () => {
    const result = await goLive({ locationId });
    if ('error' in result && result.error) {
      // The server does the real go-live check, so surface its reason verbatim.
      const message =
        'data' in result.error
          ? ((result.error.data as { error?: { message?: string } })?.error?.message ??
            'Something is still missing.')
          : 'Something is still missing.';
      dispatch(pushToast(message, 'error'));
      return;
    }
    onDone();
  };

  return (
    <Panel title="Before you go live">
      <div className="space-y-4 px-4 py-4">
        <ul className="divide-y divide-rule border-y border-rule">
          {Object.entries(CHECK_LABELS).map(([key, label]) => {
            const passed = checks?.[key as keyof typeof checks] ?? false;
            return (
              <li key={key} className="flex items-center gap-3 py-2.5 text-small">
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-sm text-micro ${
                    passed ? 'bg-jade text-white' : 'bg-rule text-slate'
                  }`}
                  aria-hidden
                >
                  {passed ? '✓' : '–'}
                </span>
                <span className={passed ? 'text-ink' : 'text-slate'}>{label}</span>
                {key === 'faqs' && !passed ? (
                  <span className="ml-auto text-micro text-slate">Optional</span>
                ) : null}
              </li>
            );
          })}
        </ul>

        <div className="rounded bg-paper px-3 py-3 text-small text-slate">
          <p className="font-medium text-ink">One last thing to do at your end</p>
          <p className="mt-1">
            Forward your existing restaurant number to the number above. Your customers keep
            dialling the number they already know, and your line answers it.
          </p>
        </div>

        {blocking.length > 0 ? (
          <p className="rounded bg-amber-soft px-3 py-2 text-small text-amber">
            Finish the unticked items above before going live.
          </p>
        ) : null}

        <Button loading={isLoading} disabled={blocking.length > 0} onClick={submit}>
          Turn on my phone line
        </Button>
      </div>
    </Panel>
  );
}
